import { useEffect, useRef, useState } from "react";
import { Dropdown, Form } from "react-bootstrap";
import { DelayFor } from "../../../shared/utilities/promise-utils";

// todo: move relevant styles to ./EditableRequestField.css
import "../RequestFeed.css";
import "./RequestDetailsModal.css";

function validate(fieldDef, value) {
  const validationMessage = fieldDef.required && (value === "" || value == null)
    ? "This field is required."
    : fieldDef.validateComplete && !fieldDef.validateComplete(value)
    ? fieldDef.invalidMessage ?? "Please enter a valid value."
    : "";
  
  // console.log('validate', fieldDef.fieldName, value, validationMessage);
  return validationMessage;
}

// coerce "true"/"false" back to booleans
function parseBoolean(value) {
  return value === "false"
    ? false
    : value === "true"
    ? true
    : value;
}

const EditableRequestField = ({ fieldDef, fieldValue, onChanged, isEditing, className, onBlur, onFocus, inputRef: externalInputRef }) => { 
  const internalInputRef = useRef(null);
  const inputRef = externalInputRef || internalInputRef;
  const toggleRef = useRef(null);
    
  const [currentValue, setCurrentValue] = useState(fieldValue);
  const [validationMessage, setValidationMessage] = useState("");

  useEffect(() => {
    if (isEditing && inputRef?.current) {
      inputRef.current.focus();      
    }
  }, [isEditing]);

  useEffect(() => {
    setCurrentValue(fieldValue);
    setValidationMessage("");
  }, [fieldValue]);

  // NOTE: validatePartial checks if the input can be immediately flagged as invalid even as the
  // user types. The idea is to prevent invalid input in e.g. a numeric field by blocking and 
  // ignoring non-numeric characters as they're typed.
  // validateComplete checks if the input is valid when the user has finished typing. This is 
  // for checking whether the form is valid and can be submitted.
  function handleModified(modifiedValue) {
    if (fieldDef.type === "select") {
      modifiedValue = parseBoolean(modifiedValue);
    }

    if (fieldDef.validatePartial && !fieldDef.validatePartial(modifiedValue)) {
      // the user tried to enter an invalid character or too many characters; ignore the input
      return;
    }  

    const validationMessage = validate(fieldDef, modifiedValue);
    setValidationMessage(validationMessage);
    setCurrentValue(modifiedValue);
    onChanged?.(modifiedValue, validationMessage);
  }

  function handleBlur() {
    // Use a short delay to give the other handlers a chance to run 
    DelayFor(10).then(() => {
      setValidationMessage(() => {
        const validationMessage = validate(fieldDef, currentValue);
        
        if (onBlur) {
          onBlur(validationMessage);
        }

        return validationMessage;
      });    
    });
  }

  function handleFocus() {
    onFocus?.(validationMessage);
  }

  return isEditing && fieldDef
    ? fieldDef.type === "select" 
      ? <Dropdown 
          className={"link-dropdown " + className + (validationMessage ? " is-invalid" : "")} 
          onSelect={handleModified} 
          onToggle={show => show ? handleFocus() : handleBlur()}
          value={currentValue || ""}
        >
          <Dropdown.Toggle ref={toggleRef} variant="link">
            {fieldDef.formatValue(currentValue) || "Select"}
          </Dropdown.Toggle>
          <Dropdown.Menu ref={inputRef}>
            {fieldDef.options.map(option => (
              <Dropdown.Item key={option.key} eventKey={option.key}>
                {option.value}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      : <Form.Control 
          ref={inputRef}
          type={fieldDef.type || "text"}
          value={currentValue || ""}
          size="sm"
          required={fieldDef.required}
          className={"editable-input " + className + (validationMessage ? " is-invalid" : "")}
          placeholder={currentValue || "(empty)"}
          onChange={e => handleModified(e.target.value)}
          onBlur={handleBlur}
          onFocus={handleFocus}
        />
    : <span className={"editable-read-only " + className}>
        {fieldDef?.formatValue(currentValue) || currentValue || "(none)"}
      </span>;
};

export default EditableRequestField;